import { orderService, paymentService, storeService } from 'config/api';
import {
  mapDataForOrderReview,
  mapDataForOrderSubmit,
  mapDataForStoreInfo,
  transformProductsForOrderReview,
} from 'utils';
import { SubmissionError } from 'redux-form';
import { extractSubmitErrorMessage } from 'modules/clients/utils';
import { addNotification } from 'shell/actions';
import { missingCardMessage } from 'modules/clients/constants';
import actionTypes from './actionTypes';

export const setDefaultShipmentAddress = (value) => {
  return {
    type: actionTypes.SET_SHIPMENT_DEFAULT,
    payload: value,
  };
};

export const setSelectedShipmentAddress = (id) => ({
  type: actionTypes.SET_SHIPMENT_SELECTED,
  payload: id,
});

/**
 * @param {Array} items - Optional items list that will be used for store pickup order review.
 * If no items provided, items from conciergeProducts will be used instead.
 * For more details see: https://jira.mynmg.com/browse/OSP-4558
 */
export const orderReview = (items = []) => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.ORDER_REVIEW_LOADING,
  });

  const { clientsModule } = getState();
  const { clientId, shippingAddress } = clientsModule.clientDetails?.data || {};

  const data = mapDataForOrderReview(clientsModule);

  if (!shippingAddress?.length) {
    // Reset shipmentMethods if no addresses were found
    dispatch({
      type: actionTypes.ORDER_REVIEW_SUCCESS,
      payload: {
        shipmentOptions: [],
        items: clientsModule.cart.conciergeProducts,
      },
    });
    return;
  }

  try {
    const itemsLength = items.length;
    const orderData = itemsLength ? { ...data, items: transformProductsForOrderReview(items) } : data;
    const response = await orderService.post('/review', orderData, { clientId });

    if (!itemsLength) {
      dispatch({
        type: actionTypes.ORDER_REVIEW_SUCCESS,
        payload: {
          shipmentOptions: response.data.shipmentOptions,
          items: clientsModule.cart.conciergeProducts,
        },
      });
    } else {
      dispatch({
        type: actionTypes.ORDER_REVIEW_STORE_PICKUP_SUCCESS,
        payload: {
          shipmentOptions: [response.data.shipmentOptions.find((method) => method.shipmentOption === 'SL3')],
          items,
        },
      });
    }
  } catch (err) {
    dispatch({
      type: actionTypes.ORDER_REVIEW_ERROR,
      payload: err,
    });
  }
};

export const checkLoyaltyCardBalance = (giftCardNumber) => async (dispatch) => {
  try {
    const res = await paymentService.post('/addGiftCard', {
      giftCardNumber,
      isLoyalty: true,
    });
    dispatch({
      type: actionTypes.UPDATE_LOYALTY_CARD_BALANCE,
      payload: res.data,
    });
    return res.data.giftCardBalance.toFixed(2);
  } catch (error) {
    return '';
  }
};

export const addGiftCardPayment = (payload) => async (dispatch, getState) => {
  const { gcPayments } = getState().clientsModule.checkout;
  const invalidCardNumberError = { giftCardNumber: 'Invalid gift card number' };
  const isCardExists = gcPayments.find((p) => p.giftCardNumber === payload.giftCardNumber);

  if (isCardExists) {
    throw new SubmissionError(invalidCardNumberError);
  }

  dispatch({ type: actionTypes.ADD_GIFT_CARD_PAYMENT });

  try {
    const { data } = await paymentService.post('/addGiftCard', payload, { httpClientOptions: { handleErrors: false } });
    dispatch({
      type: actionTypes.ADD_GIFT_CARD_PAYMENT_SUCCESS,
      payload: {
        ...payload,
        ...data,
        selected: data.giftCardBalance !== 0,
      },
    });
  } catch (err) {
    dispatch({ type: actionTypes.ADD_GIFT_CARD_PAYMENT_ERROR, payload: err });
    throw new SubmissionError({});
  }
};

export const submitOrder = (clientId, data, bopsAddress = {}) => async (dispatch) => {
  dispatch({
    type: actionTypes.SUBMIT_ORDER_LOADING,
  });

  try {
    const response = await orderService.post('/submit', mapDataForOrderSubmit({ ...data, bopsAddress }), {
      clientId,
      httpClientOptions: { handleErrors: false },
    });
    dispatch({
      type: actionTypes.SUBMIT_ORDER_SUCCESS,
      payload: response.data.orderId,
    });
  } catch (err) {
    const errorMessage = extractSubmitErrorMessage(err);
    dispatch(
      addNotification({
        id: `error-${Date.now()}`,
        message: errorMessage,
        type: 'error',
      }),
    );
    dispatch({
      type: actionTypes.SUBMIT_ORDER_ERROR,
      payload: err,
    });
  }
};

export const applyPromo = (promoCodes) => {
  return {
    type: actionTypes.APPLY_PROMO,
    payload: promoCodes,
  };
};

export const removePromo = (promoCode) => {
  return {
    type: actionTypes.REMOVE_PROMO,
    payload: promoCode,
  };
};

export const resetPromo = () => {
  return {
    type: actionTypes.RESET_PROMO,
  };
};

export const updateGCPayments = (payload) => {
  return {
    type: actionTypes.UPDATE_GIFT_CARD_PAYMENTS,
    payload,
  };
};

export const preSubmitOrder = () => (dispatch) => {
  const err = new Error();
  err.message = missingCardMessage;

  dispatch({
    type: actionTypes.SUBMIT_ORDER_ERROR,
    payload: err,
  });

  dispatch({
    type: '@SHELL_ADD_NOTIFICATION',
    payload: {
      id: `http-${Date.now()}`,
      type: 'error',
      message: err.message,
    },
  });
};

export const getStoreInformation = (storeNum) => async (dispatch) => {
  const trimmedNum = storeNum.replace(/^0+/, '');
  const formattedNum = Number(trimmedNum) < 10 ? `0${trimmedNum}` : trimmedNum;
  dispatch({
    type: actionTypes.STORE_INFORMATION_LOADING,
  });

  try {
    const { data } = await storeService.get(`/${formattedNum}`);
    dispatch({
      type: actionTypes.STORE_INFORMATION_SUCCESS,
      payload: mapDataForStoreInfo(data, storeNum),
    });
  } catch (error) {
    dispatch({
      type: actionTypes.STORE_INFORMATION_ERROR,
      payload: error,
    });
  }
};

export const setStoreInformation = (storeInfo, storeNum) => {
  return {
    type: actionTypes.STORE_INFORMATION_SUCCESS,
    payload: mapDataForStoreInfo(storeInfo, storeNum),
  };
};

export const searchStoreInformation = (searchParam) => async (dispatch) => {
  if (searchParam) {
    dispatch({ type: actionTypes.SEARCH_STORE_LOADING });
    try {
      const { data } = await storeService.get(`?freeFormAddress=${searchParam}`);
      dispatch({
        type: actionTypes.SEARCH_STORE_SUCCESS,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.SEARCH_STORE_ERROR,
        payload: error,
      });
    }
  }
};

export const clearSelectedStore = () => ({
  type: actionTypes.RESET_STORE_INFORMATION,
});

export const setStorePickupMethod = (method) => ({
  type: actionTypes.SET_STORE_PICKUP_METHOD,
  payload: method,
});
